import { useCallback, useState } from 'react';
import { OperationData } from '../../../types/OperationData';
import { logger } from '../../../utils/logger';
import { telegramApi } from '../../../utils/telegramApi';
import { addWatermark, getImageFile } from '../../utils/filesUtils';
import { useCameraRequest } from './useCameraRequest';
import { GeolocationType } from './useGeolocation';

/**
 * Параметры для загрузки фотографий.
 */
interface UploadParams {
    operation_id: string;
    task_element_id: string;
    doer_user_id: string;
}

/**
 * Хук для загрузки фотографий на сервер.
 * 
 * Ответственность:
 * - Полный цикл загрузки фотографий в S3
 * - Добавление водяных знаков
 * - Валидация параметров операции
 * - Обработка ошибок загрузки
 * - Управление состоянием загрузки
 * 
 * @returns Методы и состояние загрузки
 */
export function usePhotoUpload() {
    const [isProcessing, setIsProcessing] = useState(false);
    const { getFileUrl, uploadFile, approveDoerPhotos } = useCameraRequest();

    /**
     * Обрабатывает и отправляет все фотографии на сервер через S3 хранилище.
     * 
     * Выполняет полный цикл обработки фотографий:
     * 1. Валидация параметров операции и количества фото
     * 2. Запрос подтверждения у пользователя
     * 3. Добавление водяных знаков с геолокацией и метаданными
     * 4. Конвертация в файлы и получение pre-signed URLs
     * 5. Загрузка в S3 хранилище
     * 6. Подтверждение загрузки на бэкенде
     * 7. Автоматическое закрытие приложения
     */
    const uploadPhotos = useCallback(async (
        images: string[],
        location: GeolocationType | null,
        operationData: OperationData | null,
        params: UploadParams
    ) => {
        const { operation_id: operationId, task_element_id: taskElementId, doer_user_id: doerUserId } = params;

        if (!operationId || !taskElementId || !doerUserId) {
            logger.error('Отсутствуют обязательные параметры операции', {
                operationId,
                taskElementId,
                doerUserId
            });
            telegramApi.showAlert('Ошибка: отсутствуют параметры операции');
            return;
        }

        const minImages = operationData?.min_images || 1;
        const maxImages = operationData?.max_images || 10;

        if (images.length < minImages || images.length > maxImages) {
            logger.warn('Неверное количество фотографий', {
                imageCount: images.length,
                min: minImages,
                max: maxImages
            });
            telegramApi.showAlert(`Количество фото должно быть от ${minImages} до ${maxImages}`);
            return;
        }

        // Запрашиваем подтверждение перед отправкой
        return telegramApi.showConfirm(
            `Подтверждаете итоговую отправку ${images.length} фотографий?`
        ).then(async (confirmed) => {
            if (!confirmed) {
                return;
            }

            setIsProcessing(true);
            
            try {
                // Показываем прогресс в главной кнопке
                telegramApi.mainButton.show('Подготовка...', false);
                
                // Добавляем водяные знаки ко всем изображениям
                const watermarkedImages = await Promise.all(
                    images.map((image) => addWatermark(image, operationData || {}, location!))
                );
                
                // Конвертируем изображения в файлы
                const imgFiles = await Promise.all(watermarkedImages.map(getImageFile));
                
                // Получаем URL для загрузки каждого файла
                const uploadData = await Promise.all(imgFiles.map(async (photoFile) => {
                    const body = {
                        user_id: doerUserId,
                        name: photoFile.name,
                        size: photoFile.size,
                        mime_type: photoFile.type,
                        file_type: 'photo',
                    };

                    const response = await getFileUrl(operationId, taskElementId, body);
                    return response.data;
                }));

                telegramApi.mainButton.show('Загрузка...', false);

                // Загружаем файлы в облако
                const urls = await Promise.all(uploadData.map(async (item, index) => {
                    await uploadFile(item.url, imgFiles[index]);
                    return item.url;
                }));

                telegramApi.mainButton.show('Обработка...', false);

                // Подтверждаем загрузку фотографий
                await approveDoerPhotos(doerUserId, taskElementId, urls);
                
                telegramApi.closeApp();
            } catch (error) {
                logger.error('Ошибка при отправке на сервер', { error });
                telegramApi.showAlert('Произошла ошибка при отправке. Попробуйте еще раз.');
            } finally {
                setIsProcessing(false);
            }
        });
    }, [getFileUrl, uploadFile, approveDoerPhotos]);

    return {
        isProcessing,
        uploadPhotos
    };
}